<template>
  <ChartWrapper class="multi-value-bar" :status="status">
    <div class="root">
      <div class="marker" v-for="d in positionedData" :key="d.id+(d.compared?'-comp':'')" :style="{left: d.x+'%'}" 
        :class="{selected:selectedSector && selectedSector.id===d.id,compared:d.compared,noncompared:compareData && !d.compared}">
        <div class="value" :class="d.className" :style="{top: d.y+'%'}" @click="()=>clickHandler(d)">
          <template v-if="d.className==='top-value'">{{t(d.id)}} {{d.value}}%</template>
          <template v-else>{{d.value}}% {{t(d.id)}}</template>
        </div>
        <div class="line" :style="{top: d.y0+'%', height: d.h+'%'}"></div>
      </div>
      <div class="background-bar" :style="{top: barPos+'%', height: barHeight+'%'}"></div>
      <div class="current-bar" :style="{top: barPos+'%', width: currentPos+'%', height: barHeight+'%'}"></div>
      <div class="marker ref-marker" :style="{left: refPos+'%'}">
        <div class="top-ref-value" :style="{top: (barPos-refDist)+'%'}">{{lastYear}}</div>
        <div class="line dashed" :style="{top: (barPos-refDist)+'%', height: (barHeight+refDist*2)+'%'}"></div>
        <div class="bottom-ref-value" :style="{top: (barPos+barHeight+refDist)+'%'}">100%</div>
      </div>
      <div class="bar-cut" v-if="shouldCutBar" :style="{top: `calc(${barPos}% - 2px)`, height: `calc(${barHeight}% + 4px)`}"></div>
    </div>
  </ChartWrapper>
</template>

<script>
import d3 from '@/utils/d3_importer'
import ChartWrapper from '@/components/IndicatorCards/parts/charts/ChartWrapper'
import { mapState } from 'vuex'

const SEPARATION = 7
const BAR_HEIGHT = 35
const REF_GAP = 20
const OTULIER_THRESHOLD = 200
const LOWER_DATA_THRESHOLD = 50
export default {
  name: "MultiValueBar",
  props: {
    data:{
      required: true
    },
    compareData:{
      required: false
    },
    selected: null,
    status: null
  },
  model:{
    prop: 'selected',
    event: 'change'
  },
  components: {
    ChartWrapper
  },

  computed: {
    ...mapState(['nodeInfo']),
    refGap () {
      return REF_GAP
    },
    refDist () {
      return 4
    },
    sortedData () {
      let result
      if(this.isCompareMode){
        let main = this.data.find((d)=>d.id===this.selectedSector.id)
        if (this.compareData) {
          let compared = this.compareData.find((d)=>d.id===this.selectedSector.id)
          compared.compared = true
          result = [main,compared].sort((a,b)=>a.value-b.value)
        } else {
          result = [main]
        }
      } else {
        result = [...this.data].sort((a,b)=>a.value-b.value)
      }
      return JSON.parse(JSON.stringify(result))
    },
    lowerData () {
      if(this.compareData){
        return this.sortedData.filter((d)=>d.compared)
      } else {
        return this.sortedData.slice(0,this.sortedData.findIndex((d)=>d.value>LOWER_DATA_THRESHOLD))
      }
    },
    upperData () {
      if(this.compareData){
        return this.sortedData.filter((d)=>!d.compared)
      } else {
        return this.sortedData.slice(this.sortedData.findIndex((d)=>d.value>LOWER_DATA_THRESHOLD)).reverse()
      }
    },
    
    topy () {
      return (this.upperData.length)*SEPARATION 
    },
    bottomy () {
      return 100 - (this.lowerData.length)*SEPARATION
    },
    barHeight () {
      return this.bottomy - this.topy - REF_GAP
    },
    barPos () {
      return this.upperData.length * SEPARATION  + REF_GAP/2
    },
    positionedData () {
      let result = [...this.lowerData,...this.upperData]
      
      let topScale = d3.scaleLinear().domain([this.lowerData.length,result.length]).range([0,this.topy])
      let lowScale = d3.scaleLinear().domain([0,this.lowerData.length]).range([100,this.bottomy])

      result.forEach((d,i)=>{
        d.x = this.scale(d.value)
        d.y = this.lowerData.indexOf(d)>=0 ? lowScale(i) : topScale(i)
        d.y0 = this.lowerData.indexOf(d)>=0 ? this.barPos : d.y
        d.h = this.lowerData.indexOf(d)>=0 ? (d.y-this.barPos) : (this.barPos+this.barHeight-d.y0)
        d.className = this.lowerData.indexOf(d)>=0 ? 'bottom-value' : 'top-value'
        d.outlier = d.value>OTULIER_THRESHOLD
        d.index = i
      })
      return result
    },
    shouldCutBar(){
      return this.positionedData.some((d)=>d.outlier)
    },
    currentYear () {
      return this.$store.getters.dataCurrentYear
    },
    lastYear () {
      return this.currentYear - 1
    },
    scale () {
      let s = d3.scaleLinear().domain([0,Math.max(100,Math.min(OTULIER_THRESHOLD,d3.max(this.data,(d)=>d.value)))]).range([0,100])
      return function(x){
        if(x<OTULIER_THRESHOLD) return s(x)
        else return s(OTULIER_THRESHOLD)
      }
      // return d3.scaleLinear().domain([0,Math.max(100,d3.max(this.data,(d)=>d.value))]).range([0,100])
    },
    selectedSector () {
      return this.data.find((d)=>d.id === this.selected) || this.data.find((d)=>d.id === 'commerce-sector-total')
    },
    currentPos () {
      return this.selectedSector?this.scale(this.selectedSector.value):0
    },
    refPos () {
      return this.scale(100)
    }
  },
  
  methods:{
    clickHandler(d){
      this.$emit('change',d.id)
    }
  }
}
</script>

<style scoped lang="stylus">

.multi-value-bar
  position: relative
  
  .root
    position: absolute
    top: 50%
    transform: translate(0, -50%)
    width: 100%
    height 100%


    .background-bar
      background: #ddd
      width: 100%
      position: absolute

    .current-bar
      background: $bs-blue
      position: absolute
      transition width .3s
    .bar-cut
      position absolute
      right 30px
      width 10px
      height 24px
      top -2px
      background #fff
      transform skew(-10deg)
      &:before
        position absolute
        content:''
        border-left solid 1px #777
        top -10%
        height 120%
        // transform rotate(5deg)
      &:after
        position absolute
        content:''
        border-left solid 1px #777
        right 0
        top -10%
        height 120%
        // transform rotate(5deg)
    .marker
      position: absolute
      transition left .3s
      height 100%
      z-index: 1;
      opacity 0.3
      &.ref-marker
        opacity: 1
      &.selected
        opacity: 1
      &.compared
        color $compare-color
        .line
          border-color $compare-color
      &.noncompared
        color $bs-blue
        .line
          border-color $bs-blue

      .line
        position absolute
        border-left: solid 1px
        border-color #000
        &.dashed
          border-left: dashed 1px
          border-color #000

      .top-value,
      .bottom-value,
      .top-ref-value,
      .bottom-ref-value
        position: absolute
        white-space: nowrap
        m-font-size(10,12)
        font-family 'Lato'
        font-weight: $fw-semi-bold
        padding 0 4px
      .top-value
        transform: translate(calc(-100% + .1em),-15%);
      .bottom-value
        transform: translate(-.1em,-90%);
        
      // OPCION PAO, me gusta menos y ocupa mas...
      // .top-value
      //   transform: translate(calc(-100% + .6em), calc(-15% - 1em));
      // .bottom-value
      //   transform: translate(-.6em, calc(-90% + 1em));
      .top-ref-value,
        text-align center
        transform: translate(-50%,-100%);
      .bottom-ref-value
        text-align center
        transform: translate(-50%,0);
</style>